import { TextField } from "@material-ui/core";
import { CellParams, ColDef, DataGrid } from "@material-ui/data-grid";
import UpdatableCountField, { Button } from "components/Admin/Tables/columns/UpdatableCountField";
import { fetchProductsList, selectLoading, selectProductsIds } from "features/admin/Products/productsAdminSlice";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";
import { fetchProjectElements, setProjectElements } from "../adminGallerySlice";
import ProjectProducts from "./ProjectProducts";

interface ProductsForProjectTableProps {
  setValue: any;
  getValues: any;
  activeValues?: any;
}

const ProductsForProjectTable: React.FC<ProductsForProjectTableProps> = ({ setValue, getValues, activeValues }) => {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectLoading);
  const products = useSelector(selectProductsIds);

  const [orderedItems, setOrderedItems] = useState<any>([]);
  const [filteredRows, setFilteredRows] = useState<any[]>(products);

  useEffect(() => {
    setFilteredRows(
      products.map((item) => {
        return { ...item, ordered: 0 };
      })
    );
  }, [products]);

  useEffect(() => {
    if (products.length === 0) {
      dispatch(fetchProductsList());
    }
    if (activeValues) {
      const items = activeValues.items;
      dispatch(
        setProjectElements(
          items.map((item: any) => {
            return { product: item, count: item.count };
          })
        )
      );
      setValue("items", [
        ...items.map((item: any) => {
          return { id: item.refId, count: item.count };
        }),
      ]);
    } else {
      setValue("items", []);
    }

    return () => {
      dispatch(setProjectElements([]));
    };
  }, []);

  useEffect(() => {
    if (orderedItems.length !== 0) {
      dispatch(fetchProjectElements(orderedItems));
    }
  }, [orderedItems]);

  const getUpdateItem = (params: any) => {
    const newFilteredRows = [...filteredRows];
    const itemToUpdate = newFilteredRows.find((item) => item.id === params.row.id);

    const updateRows = () => {
      setFilteredRows(newFilteredRows);
    };

    return { itemToUpdate, updateRows };
  };

  const minusHandler = (params: CellParams) => {
    const { itemToUpdate, updateRows } = getUpdateItem(params);
    if (itemToUpdate) {
      itemToUpdate.ordered = itemToUpdate.ordered - 1;
      updateRows();
    }
  };

  const plusHandler = (params: CellParams) => {
    const { itemToUpdate, updateRows } = getUpdateItem(params);
    if (itemToUpdate) {
      itemToUpdate.ordered = itemToUpdate.ordered + 1;
      updateRows();
    }
  };

  const columns: ColDef[] = [
    { sortable: false, field: "title", headerName: "Име", width: 700 },
    {
      field: "ordered",
      headerName: "Брой",
      width: 200,
      renderCell: (params) => {
        return (
          <UpdatableCountField
            onMinusClickHandler={minusHandler}
            onPlusClickHandler={plusHandler}
            rowParams={params}
            isMinusDisabled={params.row.ordered === 0}
          />
        );
      },
    },
    {
      field: "action",
      headerName: "Действие",
      width: 200,
      renderCell: (params) => {
        return (
          <Button onClick={(event) => addProductToOrder(event, params)} disabled={params.row.ordered === 0}>
            Добави
          </Button>
        );
      },
    },
  ];

  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchWord = event.target.value;
    const newRows = products.filter((item: any) => item.title.toLowerCase().includes(searchWord.toLowerCase()));
    setFilteredRows(newRows);
  };

  const addProductToOrder = (ev: any, params: any) => {
    ev.preventDefault();
    const currentItems = getValues("items");
    const itemToAdd = currentItems.find((item: any) => item.id === params.row.refId);
    if (itemToAdd) {
      itemToAdd.count += params.row.ordered;
    } else {
      const orderItem = { id: params.row.refId, count: params.row.ordered };
      currentItems.push(orderItem);
    }
    setFilteredRows(
      filteredRows.map((item) => {
        return item.refId === params.row.refId ? { ...item, ordered: 0 } : { ...item };
      })
    );
    setOrderedItems([...currentItems]);
  };

  const removeItemHandler = (id: string) => {
    const currentItems: any[] = getValues("items");
    const itemIdx = currentItems.findIndex((item: any) => item.id === id);
    if (itemIdx !== -1) {
      currentItems.splice(itemIdx, 1);
    }

    if (currentItems.length === 0) {
      setValue("items", []);
      dispatch(fetchProjectElements([]));
    }

    setOrderedItems([...currentItems]);
  };

  return (
    <ProductsForProjectTableContainer>
      <SearchField size="small" variant="outlined" label="Търси" id="outlined-basic" onChange={onSearch} />
      <TableContainer>
        <DataGrid
          columns={columns}
          autoPageSize={true}
          rows={filteredRows}
          disableSelectionOnClick
          disableColumnFilter
          loading={isLoading}
        />
      </TableContainer>
      <ProjectProducts noProductsMessage="Няма климатици добавени към проекта" removeItemHandler={removeItemHandler} />
    </ProductsForProjectTableContainer>
  );
};

const ProductsForProjectTableContainer = styled.div`
  width: 100%;
  display: flex;
  flex-flow: column;
`;

const TableContainer = styled.div`
  width: 100%;
  height: 400px;
`;

const SearchField = styled(TextField)`
  width: 320px;
  margin: 10px 0;
`;

export default ProductsForProjectTable;
